import type { ContactRow } from '@profiler/shared'

const DATE_TIME = new Intl.DateTimeFormat(undefined, {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

const DATE_ONLY = new Intl.DateTimeFormat(undefined, { day: '2-digit', month: 'short', year: 'numeric' })

/**
 * The clock time on its own, to sit under a date in a table cell.
 *
 * A batch of contacts lands in one afternoon, so a column of dates is a column
 * of the same date — the time is the only part that distinguishes one row from
 * the next, and it was only in a tooltip nobody hovers.
 */
const TIME_ONLY = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' })

const NUMBER = new Intl.NumberFormat(undefined)

export const EM_DASH = '—'

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return EM_DASH
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? EM_DASH : DATE_TIME.format(d)
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return EM_DASH
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? EM_DASH : DATE_ONLY.format(d)
}

export function formatTime(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : TIME_ONLY.format(d)
}

export function formatNumber(n: number | null | undefined): string {
  return typeof n === 'number' && Number.isFinite(n) ? NUMBER.format(n) : EM_DASH
}

/** "mean (p10 to p90)". The band is the point, so it is never dropped. */
export function formatEstimate(estimate: { mean: number; p10: number; p90: number }): string {
  return `${formatNumber(estimate.mean)} (${formatNumber(estimate.p10)} to ${formatNumber(estimate.p90)})`
}

/**
 * Name columns come from the API's own first/last fields. Splitting fullName on
 * whitespace mangles compound surnames and titles, and the plan forbids it for
 * the export; the UI must not disagree with the export about who someone is.
 */
export function contactName(contact: Pick<ContactRow, 'fullName' | 'firstName' | 'lastName'>): string {
  if (contact.fullName?.trim()) return contact.fullName.trim()
  const parts = [contact.firstName, contact.lastName].filter((p): p is string => Boolean(p?.trim()))
  return parts.length > 0 ? parts.join(' ') : EM_DASH
}

/**
 * The LinkedIn profile a contact key is, or null.
 *
 * A contact's key is a LinkedIn profile URL for people found on LinkedIn, and
 * `crm:zoho:contact:<id>` for people only the CRM holds. A LinkedIn column
 * shows the first and nothing else: a CRM key rendered there reads as a
 * LinkedIn identity the person does not have.
 */
export function linkedinProfileOf(key: string | null | undefined): string | null {
  if (!key) return null
  return /^https:\/\/www\.linkedin\.com\/in\/[^/?#\s]+\/?$/i.test(key.trim()) ? key.trim() : null
}

/** `in/some-slug` reads better in a cell than the full URL. A dash for anything that is not a LinkedIn profile. */
export function shortLinkedin(url: string | null | undefined): string {
  const profile = linkedinProfileOf(url)
  if (!profile) return EM_DASH
  return profile.replace(/^https?:\/\/(www\.)?linkedin\.com\//i, '').replace(/\/$/, '') || profile
}

export function percent(part: number, total: number): number {
  if (!Number.isFinite(total) || total <= 0) return 0
  return Math.min(100, Math.max(0, (part / total) * 100))
}

/**
 * A camelCase identifier as words: `someNewFacet` becomes `some new facet`.
 *
 * Only a lower-case letter or digit followed by a capital is split, so an
 * acronym (`CEO`) stays whole. Kept out of humanise(), which reads statuses and
 * reasons that are snake_case already and values like `LinkedIn` that must not
 * be split.
 */
export function splitCamelCase(value: string): string {
  return value.replace(
    /([a-z0-9])([A-Z])/g,
    (_, before: string, capital: string) => `${before} ${capital.toLowerCase()}`,
  )
}

/** Turns `unverified_domain_mismatch` into `Unverified domain mismatch`. */
export function humanise(value: string | null | undefined): string {
  if (!value) return EM_DASH
  const spaced = value.replaceAll('_', ' ').trim()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/**
 * "accepted by Ada" — the status, and who decided it.
 *
 * The AI reviewer is a part of this product rather than a model anybody has to
 * recognise, so an `ai:` decider reads as "the reviewer" and the model id stays
 * in the reasoning line. Written out identically on two screens before.
 */
export function decidedLabel(status: string, decidedBy: string | null | undefined): string {
  if (!decidedBy) return humanise(status)
  const who = decidedBy.startsWith('ai:') ? 'the reviewer' : decidedBy
  return `${humanise(status)} by ${who}`
}

/**
 * How alarming a credit-cap figure is.
 *
 * Over the cap is an error whatever the caller renders; approaching it is a
 * warning. The quiet case differs by component — a Chip wants `default` and a
 * LinearProgress wants `primary` — so it is the caller's to name.
 */
export function capTone<T extends string>(used: number, quiet: T): 'error' | 'warning' | T {
  if (used >= 100) return 'error'
  return used >= 80 ? 'warning' : quiet
}

/** The reviewer's verdict as a colour. Written out on two screens before. */
export function verdictTone(verdict: string | null | undefined): 'success' | 'error' | 'default' {
  if (verdict === 'accept') return 'success'
  return verdict === 'reject' ? 'error' : 'default'
}

/**
 * How much to trust a precision figure.
 *
 * The thresholds are the same wherever this is shown; only the palette differs,
 * because one caller needs an `sx` colour and the other a component `color`.
 */
export function precisionTone<T extends string>(precision: number, good: T, fair: T, poor: T): T {
  if (precision >= 0.95) return good
  return precision >= 0.8 ? fair : poor
}
